import { normalizeAwardShowImageUrl } from "./utils";

export const LIVE_RESULTS_POLL_INTERVAL_MS = 5_000;

export type StageDisplayMode = "trophy" | "nominee" | "winners";

export interface StageFeaturedWinner {
  categoryId: string;
  categoryName: string;
  winnerName: string;
  imageUrl: string | null;
  announcedAt: string;
}

export interface StageDisplay {
  mode: StageDisplayMode;
  featured: StageFeaturedWinner | null;
}

interface LiveResultPosterInput {
  categoryId: string;
  categoryName: string;
  winnerName: string;
  winnerImageUrl: string | null;
  announcedAt: string;
}

interface NomineePosterInput {
  id: string;
  name: string;
  imageUrl: string | null;
}

interface CategoryPosterInput {
  id: string;
  nominees: NomineePosterInput[];
}

function isPersonCropUrl(url: string): boolean {
  return url.includes("/h632/");
}

export function firstNomineePosterUrl(
  imageUrl: string | null | undefined,
): string | null {
  if (!imageUrl) {
    return null;
  }

  const urls = imageUrl
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .sort((left, right) => {
      const leftPerson = isPersonCropUrl(left);
      const rightPerson = isPersonCropUrl(right);
      if (leftPerson === rightPerson) {
        return 0;
      }
      return leftPerson ? 1 : -1;
    });

  for (const url of urls) {
    const normalized = normalizeAwardShowImageUrl(url);
    if (normalized) {
      return normalized;
    }
  }

  return null;
}

export function normalizePosterUrls(
  rawUrls: readonly (string | null | undefined)[],
  limit?: number,
): string[] {
  const seen = new Set<string>();
  const posters: string[] = [];

  for (const raw of rawUrls) {
    if (!raw) {
      continue;
    }
    const trimmed = raw.trim();
    const poster =
      trimmed.startsWith("data:") || trimmed.startsWith("blob:")
        ? trimmed
        : firstNomineePosterUrl(trimmed);
    if (!poster || seen.has(poster)) {
      continue;
    }
    seen.add(poster);
    posters.push(poster);
    if (limit !== undefined && posters.length >= limit) {
      break;
    }
  }

  return posters;
}

function posterImageForCategory(
  live: LiveResultPosterInput,
  categories: CategoryPosterInput[],
  winnersByCategory: Record<string, string[]>,
): string | null {
  const fromLive = firstNomineePosterUrl(live.winnerImageUrl);
  if (fromLive) {
    return fromLive;
  }

  const category = categories.find((item) => item.id === live.categoryId);
  const winnerIds = winnersByCategory[live.categoryId] ?? [];
  for (const winnerId of winnerIds) {
    const nominee = category?.nominees.find((item) => item.id === winnerId);
    const fromNominee = firstNomineePosterUrl(nominee?.imageUrl);
    if (fromNominee) {
      return fromNominee;
    }
  }

  return null;
}

export function winnerPostersForParty(input: {
  liveResults: LiveResultPosterInput[];
  categories: CategoryPosterInput[];
  winnersByCategory: Record<string, string[]>;
}): StageFeaturedWinner[] {
  return [...input.liveResults]
    .sort(
      (left, right) =>
        new Date(left.announcedAt).getTime() -
        new Date(right.announcedAt).getTime(),
    )
    .map((live) => ({
      categoryId: live.categoryId,
      categoryName: live.categoryName,
      winnerName: live.winnerName,
      imageUrl: posterImageForCategory(
        live,
        input.categories,
        input.winnersByCategory,
      ),
      announcedAt: live.announcedAt,
    }));
}

export function resolveStageDisplay(input: {
  posters: StageFeaturedWinner[];
  totalCategories: number;
}): StageDisplay {
  const featured = input.posters[input.posters.length - 1] ?? null;
  if (!featured) {
    return { mode: "trophy", featured: null };
  }

  const complete =
    input.totalCategories > 0 && input.posters.length >= input.totalCategories;

  return {
    mode: complete ? "winners" : "nominee",
    featured: complete ? null : featured,
  };
}
